import React, { useState, useEffect, useRef } from "react";
import styles from "./StandardContentCardInfoDropdown.module.css";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faSort,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import ChevronButton from "../ChevronButton";
import TextInput from "../TextInput";
import SelectNumberInput from "../SelectNumberInput";
import Button from "../Button";
import ShapeButton from "../ShapeButton";
import SelectColorInput from "../SelectColorInput";
import RadioButtonGroup from "../RadioButtonGroup";
import ButtonStyledRadioButtonGroup from "../ButtonStyledRadioButtonGroup";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import SelectPhotoModal from "../SelectPhotoModal";

/**
 * Interactive component.
 *  Dropdown that allows a Standard Content feed’s card information to be modified or created.
 */
export function StandardContentCardInfoDropdown({
  cardInformation,
  setCardInformation,
  isOpen,
  onClick,
  setCurrentDropdown,
}) {
  // const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen);
  const [activeColorPicker, setActiveColorPicker] = useState(null);

  const [imgSrcValue, setImgSrcValue] = useState(cardInformation.imgSrc);
  // Keep local imgSrcValue in sync with incoming cardInformation changes
  useEffect(() => {
    setImgSrcValue(cardInformation.imgSrc ?? "");
  }, [cardInformation.imgSrc]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const numberOptions = Array.from({ length: 21 }, (_, i) =>
    ((i * 0.05 * 100) / 100).toFixed(2)
  );

  const angleOptions = Array.from({ length: 360 / 15 + 1 }, (_, i) => i * 15);
  const angleOptionsString = angleOptions.map(String);

  const [customColors, setCustomColors] = useState({
    headerTextCustomColor: "#fff",
    headerBackgroundCustomColor: "#fff",
    subHeaderCustomColor: "#fff",
    firstGradientCustomColor: "#006837",
    secondGradientCustomColor: "#fff",
  });

  // Gradient colors
  // const gradientColors = cardInformation.gradientColors;
  const defaultGradientColors = ["default #006837", "default #fff"];
  const gradientColors =
    cardInformation.gradientColors ?? defaultGradientColors;

  const [buttons, setButtons] = useState(() =>
    (gradientColors || []).map((color, idx) => ({
      customColor:
        idx === 0
          ? customColors.firstGradientCustomColor
          : idx === 1
          ? customColors.secondGradientCustomColor
          : "#fff",
      currentColor: color,
      isOpen: false,
    }))
  );

  // Keep buttons in sync with gradientColors length
  useEffect(() => {
    setButtons((prevButtons) => {
      // If gradientColors changes length, update buttons accordingly
      if (!gradientColors) return [];
      if (prevButtons.length === gradientColors.length) {
        // Only update currentColor if colors changed
        return prevButtons.map((btn, idx) => ({
          ...btn,
          currentColor: gradientColors[idx],
        }));
      }
      // Remap all
      return gradientColors.map((color, idx) => ({
        customColor:
          idx === 0
            ? customColors.firstGradientCustomColor
            : idx === 1
            ? customColors.secondGradientCustomColor
            : "#fff",
        currentColor: color,
        isOpen: false,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gradientColors.length,
    gradientColors.join(","),
    customColors.firstGradientCustomColor,
    customColors.secondGradientCustomColor,
  ]);

  // Ref for the div that contains color selectors (defined and set in SelectColorInput)
  const [headerTextColorRef, setHeaderTextColorRef] = useState(null);
  const [headerBackgroundRef, setHeaderBackgroundRef] = useState(null);
  const [subHeaderTextColorRef, setSubHeaderTextColorRef] = useState(null);
  const [gradientColorRefs, setGradientColorRefs] = useState(
    buttons.map(() => null)
  );

  useEffect(() => {
    function handleOutsideClick(event) {
      const clickedInsideHeaderTextColor =
        headerTextColorRef && headerTextColorRef.contains(event.target);
      const clickedInsideHeaderBackground =
        headerBackgroundRef && headerBackgroundRef.contains(event.target);
      const clickedInsideSubHeaderTextColor =
        subHeaderTextColorRef && subHeaderTextColorRef.contains(event.target);
      let passedGradientColorTests = false;
      if (
        (activeColorPicker
          ? activeColorPicker.startsWith("gradientColor")
          : true) &&
        gradientColorRefs &&
        gradientColorRefs.every((ref) => !(ref && ref.contains(event.target)))
      ) {
        passedGradientColorTests = true;
      }
      if (
        activeColorPicker !== "headerColor" &&
        !clickedInsideHeaderTextColor &&
        activeColorPicker !== "headerBackground" &&
        !clickedInsideHeaderBackground &&
        activeColorPicker !== "subHeaderTextColor" &&
        !clickedInsideSubHeaderTextColor &&
        passedGradientColorTests
      ) {
        setActiveColorPicker(null);
      }
    }
    document.addEventListener("mouseup", handleOutsideClick);
    return () => {
      document.removeEventListener("mouseup", handleOutsideClick);
    };
  }, [buttons, headerTextColorRef, gradientColorRefs]);

  //Function to get the input values
  const handleCardInfoChange = (field, value) => {
    const updatedCardInformation = {
      ...cardInformation,
      [field]: value,
    };

    setCardInformation(updatedCardInformation, "cardInformation");
  };

  // Adding new gradient color to the array.
  const addNewButton = () => {
    const newButton = {
      customColor: "#fff",
      currentColor: "default #FFF",
      isOpen: false,
    };
    setButtons([...buttons, newButton]);
    handleCardInfoChange("gradientColors", [
      ...cardInformation.gradientColors,
      newButton.currentColor,
    ]);
  };

  // Delete gradient color from the array.
  const deleteButton = (index) => {
    if (buttons.length <= 2) {
      // Don't allow fewer than 2 gradient colors
      return;
    }
    
    const newButtons = [...buttons];
    newButtons.splice(index, 1);
    setButtons(newButtons);
    let newGradientColors = [];
    for (let ctr = 0; ctr < newButtons.length; ctr++) {
      newGradientColors = [...newGradientColors, newButtons[ctr].currentColor];
    }
    handleCardInfoChange("gradientColors", newGradientColors);
  };

  // Handles custom color in gradient array
  const handleCustomColorChange = (index, colorValue) => {
    const newButtons = [...buttons];
    newButtons[index].customColor = colorValue;
    setButtons(newButtons);

    const newGradientColors = [...cardInformation.gradientColors];
    newGradientColors[index] = colorValue;
    handleCardInfoChange("gradientColors", newGradientColors);
  };

  // Handles current color in gradient array
  const handleCurrentColorChange = (index, e) => {
    const newButtons = [...buttons];
    newButtons[
      index
    ].currentColor = `${e.target.title} ${e.target.style.backgroundColor}`;
    newButtons[index].isOpen = false;
    setButtons(newButtons);

    const newGradientColors = [...cardInformation.gradientColors];
    newGradientColors[
      index
    ] = `${e.target.title} ${e.target.style.backgroundColor}`;
    handleCardInfoChange("gradientColors", newGradientColors);
  };

  // Handles select color input in gradient array
  const handleIsOpenChange = (index, isOpen) => {
    const pickerId = "gradientColor-" + index;
    setActiveColorPicker(isOpen ? pickerId : null);
  };

  // Submit selected photo in change image modal
  const handleModalSubmit = (photoSrc) => {
    const next = photoSrc ?? "";
    setImgSrcValue(next);
    handleCardInfoChange("imgSrc", next);
    setIsModalOpen(false);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setImgSrcValue(cardInformation.imgSrc);
  };

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={isOpen ? "dropdown" : "button"}
        classNames={{
          enter: styles["fade-enter"],
          exit: styles["fade-exit"],
          enterActive: styles["fade-enter-active"],
          exitActive: styles["fade-exit-active"],
        }}
        addEndListener={(node, done) => {
          node.addEventListener("transitionend", done, false);
        }}
      >
        <div className={styles.container}>
          {!isOpen ? (
            <section
              onClick={() => onClick(isOpen ? "close" : "info")}
              className={styles["outside-container"]}
            >
              <div className={styles["card-dropdown"]}>
                <h3 className={styles.title}>Card Information:</h3>
                <ChevronButton
                  orientation={"right"}
                  onClick={() => onClick(isOpen ? "close" : "info")}
                  className={styles.chevronIcon}
                />
              </div>
              <div className={styles["outside-content"]}>
                <h4 className={styles.header}>{cardInformation.header}</h4>
                <p className={styles.subHeader}>{cardInformation.subHeader}</p>
                <hr className={styles.hr} />
              </div>
            </section>
          ) : (
            <section>
              <div className={styles["inside-content"]}>
                <h3 className={styles.title}>Card Information:</h3>
                <ChevronButton
                  orientation={isOpen ? "down" : "right"}
                  onClick={() => onClick(isOpen ? "close" : "info")}
                  className={styles.chevronIcon}
                />
              </div>
              <div className={styles["header-container"]}>
                <TextInput
                  label={"Header"}
                  placeholder={"Header"}
                  details={""}
                  value={cardInformation.header}
                  setValue={(e) =>
                    handleCardInfoChange("header", e.target.value)
                  }
                />
                <div className={styles["input-container"]}>
                  <SelectColorInput
                    setRef={setHeaderTextColorRef}
                    isOpen={activeColorPicker === "headerColor"}
                    setIsOpen={() => {
                      setActiveColorPicker(
                        activeColorPicker === "headerColor"
                          ? null
                          : "headerColor"
                      );
                    }}
                    customColor={customColors.headerTextCustomColor}
                    setCustomColor={(value) =>
                      setCustomColors({
                        ...customColors,
                        headerTextCustomColor: value,
                      })
                    }
                    currentColor={cardInformation.headerTextColor}
                    setCurrentColor={(e) => {
                      handleCardInfoChange(
                        "headerTextColor",
                        `${e.target.title} ${e.target.style.backgroundColor}`
                      );
                    }}
                    labelText={"Text"}
                    className={styles["text-color"]}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </div>

                <div className={styles["input-container"]}>
                  <SelectColorInput
                    setRef={setHeaderBackgroundRef}
                    isOpen={activeColorPicker === "headerBackground"}
                    setIsOpen={() => {
                      setActiveColorPicker(
                        activeColorPicker === "headerBackground"
                          ? null
                          : "headerBackground"
                      );
                    }}
                    customColor={customColors.headerBackgroundCustomColor}
                    setCustomColor={(value) =>
                      setCustomColors({
                        ...customColors,
                        headerBackgroundCustomColor: value,
                      })
                    }
                    currentColor={
                      cardInformation.headerBackground.backgroundColor
                    }
                    setCurrentColor={(e) => {
                      handleCardInfoChange("headerBackground", {
                        ...cardInformation.headerBackground,
                        backgroundColor: `${e.target.title} ${e.target.style.backgroundColor}`,
                      });
                    }}
                    labelText={"BG"}
                    className={styles.bgColor}
                  />

                  <SelectNumberInput
                    value={cardInformation.headerBackground.backgroundOpacity}
                    onSelect={(e) =>
                      handleCardInfoChange("headerBackground", {
                        ...cardInformation.headerBackground,
                        backgroundOpacity: e.target.value,
                      })
                    }
                    numberOptions={[...numberOptions].reverse()}
                  />
                </div>
              </div>

              <div className={styles["subheader-container"]}>
                <TextInput
                  label={"Sub Header"}
                  placeholder={"Header"}
                  value={cardInformation.subHeader}
                  setValue={(e) => {
                    handleCardInfoChange("subHeader", e.target.value);
                  }}
                />

                <div className={styles["input-container"]}>
                  <SelectColorInput
                    setRef={setSubHeaderTextColorRef}
                    isOpen={activeColorPicker === "subHeaderTextColor"}
                    setIsOpen={() => {
                      setActiveColorPicker(
                        activeColorPicker === "subHeaderTextColor"
                          ? null
                          : "subHeaderTextColor"
                      );
                    }}
                    customColor={customColors.subHeaderCustomColor}
                    setCustomColor={(value) =>
                      setCustomColors({
                        ...customColors,
                        subHeaderCustomColor: value,
                      })
                    }
                    currentColor={cardInformation.subHeaderTextColor}
                    setCurrentColor={(e) => {
                      handleCardInfoChange(
                        "subHeaderTextColor",
                        `${e.target.title} ${e.target.style.backgroundColor}`
                      );
                    }}
                    labelText={"Text"}
                    className={styles["text-color"]}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              <hr className={styles.hr} />

              <div className={styles["image-container"]}>
                <div className={styles["card-image"]}>Card Image</div>

                <RadioButtonGroup
                  currentlySelected={cardInformation.backgroundType}
                  setCurrentlySelected={(value) => {
                    handleCardInfoChange("backgroundType", value);
                  }}
                  options={["On Gradient", "Full Background"]}
                />
              </div>
              <div className={styles["change-input-image"]}>
                <TextInput
                  label={""}
                  placeholder={
                    "https://wwgus.blob.core.windows.net/icons/bsm-image-coming-soon.png"
                  }
                  value={cardInformation.imgSrc ?? ""}
                  setValue={(e) => {
                    const next = e?.target?.value ?? "";
                    setImgSrcValue(next);
                    handleCardInfoChange("imgSrc", next);
                  }}
                />
                <Button
                  text={"Change Image"}
                  className={styles.button}
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                />
              </div>

              {cardInformation.backgroundType === "On Gradient" ? (
                <>
                  <div className={styles["button-styled"]}>
                    <ButtonStyledRadioButtonGroup
                      options={[
                        {
                          value: "left",
                          buttonContent: <FontAwesomeIcon icon={faAlignLeft} />,
                        },
                        {
                          value: "center",
                          buttonContent: (
                            <FontAwesomeIcon icon={faAlignCenter} />
                          ),
                        },
                        {
                          value: "right",
                          buttonContent: (
                            <FontAwesomeIcon icon={faAlignRight} />
                          ),
                        },
                      ]}
                      currentlySelected={cardInformation.horizontalAlign}
                      setCurrentlySelected={(e) => {
                        handleCardInfoChange("horizontalAlign", e.target.value);
                      }}
                    />

                    <div className={styles["align-container"]}>
                      <div className={styles["padding-container"]}>
                        <button
                          className={classNames(styles["button-padding"], {
                            [styles.active]: cardInformation.bottomPadding,
                          })}
                          onClick={() =>
                            handleCardInfoChange("bottomPadding", true)
                          }
                        >
                          <FontAwesomeIcon icon={faSort} />
                        </button>
                        <button
                          className={classNames(styles["button-padding"], {
                            [styles.active]: !cardInformation.bottomPadding,
                          })}
                          onClick={() =>
                            handleCardInfoChange("bottomPadding", false)
                          }
                        >
                          <FontAwesomeIcon icon={faSortDown} />
                        </button>
                      </div>

                      <div className={styles["height-container"]}>
                        <h5 className={styles.height}>Max Height:</h5>
                        <TextInput
                          label={""}
                          placeholder={""}
                          value={cardInformation.maxHeight}
                          setValue={(e) =>
                            handleCardInfoChange("maxHeight", e.target.value)
                          }
                        />
                        <p className={styles.px}>px</p>
                      </div>
                    </div>
                  </div>

                  <div className={styles["gradient-container"]}>
                    <h5 className={styles.gradient}>
                      Gradient (must have at least two colors)
                    </h5>
                    <div className={styles["colors-container"]}>
                      <SelectNumberInput
                        labelText={"∡"}
                        value={cardInformation.gradientAngle}
                        onSelect={(e) =>
                          handleCardInfoChange("gradientAngle", e.target.value)
                        }
                        numberOptions={angleOptionsString}
                      />

                      <div className={styles["add-color"]}>
                        {buttons.map((button, index) => (
                          <div
                            className={styles["colors-container"]}
                            key={index}
                          >
                            <span className={styles["shape-button"]}>
                              <ShapeButton
                                shape={"round"}
                                onClick={() => deleteButton(index)}
                              />
                            </span>

                            <div className={styles["gradient-colors"]}>
                              <SelectColorInput
                                setRef={(ref) => {
                                  setGradientColorRefs((prevRefs) => {
                                    const updatedRefs = [...prevRefs];
                                    updatedRefs[index] = ref;
                                    return updatedRefs;
                                  });
                                }}
                                isOpen={
                                  activeColorPicker === "gradientColor-" + index
                                }
                                setIsOpen={() => {
                                  handleIsOpenChange(
                                    index,
                                    activeColorPicker !==
                                      "gradientColor-" + index
                                  );
                                }}
                                customColor={button.customColor}
                                setCustomColor={(value) => {
                                  handleCustomColorChange(index, value);
                                }}
                                currentColor={button.currentColor}
                                setCurrentColor={(e) => {
                                  handleCurrentColorChange(index, e);
                                }}
                                className={styles.secondColor}
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        ))}

                        <span className={styles["shape-button"]}>
                          <ShapeButton
                            shape={"square"}
                            onClick={addNewButton}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles["full-background-button"]}>
                  <ButtonStyledRadioButtonGroup
                    options={[
                      {
                        value: "left",
                        buttonContent: <FontAwesomeIcon icon={faAlignLeft} />,
                      },
                      {
                        value: "center",
                        buttonContent: <FontAwesomeIcon icon={faAlignCenter} />,
                      },
                      {
                        value: "right",
                        buttonContent: <FontAwesomeIcon icon={faAlignRight} />,
                      },
                    ]}
                    currentlySelected={cardInformation.horizontalAlign}
                    setCurrentlySelected={(e) => {
                      handleCardInfoChange("horizontalAlign", e.target.value);
                    }}
                  />
                </div>
              )}
            </section>
          )}

          <SelectPhotoModal
            currentPhotoSrc={imgSrcValue}
            setCurrentPhotoSrc={setImgSrcValue}
            isVisible={isModalOpen}
            setIsVisible={onCloseModal}
            onSubmit={handleModalSubmit}
            disableOnErrorMsg={true}
          />
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
}

StandardContentCardInfoDropdown.propTypes = {
  /**
   * Object that contains a Standard Content feed item’s card information.
   */
  cardInformation: PropTypes.shape({
    header: PropTypes.string,
    headerTextColor: PropTypes.string,
    headerBackground: PropTypes.shape({
      backgroundColor: PropTypes.string,
      backgroundOpacity: PropTypes.string,
    }),
    subHeader: PropTypes.string,
    subHeaderTextColor: PropTypes.string,
    backgroundType: PropTypes.oneOf(["On Gradient", "Full Background"]),
    imgSrc: PropTypes.string,
    horizontalAlign: PropTypes.oneOf(["left", "center", "right"]),
    bottomPadding: PropTypes.bool,
    maxHeight: PropTypes.string,
    gradientAngle: PropTypes.string,
    gradientColors: PropTypes.arrayOf(PropTypes.string),
  }),
  /**
   *Function that allows the cardInformation to be reassigned.
   */
  setCardInformation: PropTypes.func,
  /**
   * Boolean that determines if the dropdown is expanded.
   */
  isOpen: PropTypes.bool,
  /**
   * Sets the value of currentDropdown on change.
   */
  setCurrentDropdown: PropTypes.func,
};

StandardContentCardInfoDropdown.defaultProps = {
  cardInformation: {
    header: "* Card Title *",
    headerTextColor: "default #FFF",
    headerBackground: {
      backgroundColor: "default #FFF",
      backgroundOpacity: "0.80",
    },
    subHeader: "",
    subHeaderTextColor: "default #FFF",
    backgroundType: "On Gradient",
    imgSrc:
      "https://wwgus.blob.core.windows.net/icons/bsm-image-coming-soon.png",
    horizontalAlign: "center",
    bottomPadding: true,
    maxHeight: "",
    gradientAngle: "45",
    gradientColors: ["default #006837", "default #fff"],
  },
};
